import {
  Links,
  Meta,
  Scripts,
  ScrollRestoration,
  useRouteLoaderData,
} from '@remix-run/react';
import { type PropsWithChildren, useEffect } from 'react';
import {
  PreventFlashOnWrongTheme,
  type Theme,
  ThemeProvider,
  useTheme,
} from 'remix-themes';
import { env } from '~/config/env';
import { cn } from '~/utils/cn';

export function Layout({ children }: PropsWithChildren) {
  const data = useRouteLoaderData<{ theme: string | null }>('root');
  let theme = data?.theme;

  if (typeof window !== 'undefined') {
    if (theme) {
      localStorage.setItem('theme', theme);
    } else {
      theme = localStorage.getItem('theme');
    }
  }

  return (
    <ThemeProvider specifiedTheme={theme as Theme} themeAction="/api/set-theme">
      <InnerLayout ssrTheme={Boolean(theme)}>{children}</InnerLayout>
    </ThemeProvider>
  );
}

interface InnerLayoutProps {
  ssrTheme: boolean;
}

function InnerLayout({
  ssrTheme,
  children,
}: PropsWithChildren<InnerLayoutProps>) {
  const [theme] = useTheme();

  useEffect(() => {
    injectGTMScript(env.VITE_GTM_ID);
  }, []);

  return (
    <html lang="en" className={cn(theme)}>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <title>{env.VITE_APP_NAME}</title>
        <Meta />
        <Links />
        <PreventFlashOnWrongTheme ssrTheme={ssrTheme} />
        <script
          dangerouslySetInnerHTML={{
            __html: `
              (function(c,l,a,r,i,t,y){
                c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
                t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
                y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
              })(window, document, "clarity", "script", "${env.VITE_CLARITY_ID}");
            `,
          }}
        />
        <script
          dangerouslySetInnerHTML={{
            __html: `
              <!-- Meta Pixel Code -->
              !function(f,b,e,v,n,t,s)
              {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
              n.callMethod.apply(n,arguments):n.queue.push(arguments)};
              if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
              n.queue=[];t=b.createElement(e);t.async=!0;
              t.src=v;s=b.getElementsByTagName(e)[0];
              s.parentNode.insertBefore(t,s)}(window, document,'script',
              'https://connect.facebook.net/en_US/fbevents.js');
              fbq('init', '${env.VITE_FACEBOOK_PIXEL_ID}');
              fbq('track', 'PageView');
              <!-- End Meta Pixel Code --> 
            `,
          }}
        />
      </head>
      <body>
        <noscript><img height="1" width="1" style={{ display: 'none' }} src={`https://www.facebook.com/tr?id=${env.VITE_FACEBOOK_PIXEL_ID}&ev=PageView&noscript=1`}/></noscript>
        <noscript>
          <iframe
            title="GTM"
            src={`https://www.googletagmanager.com/ns.html?id=${env.VITE_GTM_ID}`}
            height="0"
            width="0"
            style={{ display: 'none', visibility: 'hidden' }}
          />
        </noscript>
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

let gtmScriptInjected = false;

function injectGTMScript(GTM_ID: string) {
  if (!GTM_ID || gtmScriptInjected) {
    return;
  }

  // Code copied from GTM console + added type annotations.
  ((w: Window, d: Document, s: 'script', l: string, i: string) => {
    w[l] = w[l] || [];
    w[l].push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js',
    });
    const f = d.getElementsByTagName(s)[0];
    const j = d.createElement<'script'>(s);
    const dl = l !== 'dataLayer' ? `&l=${l}` : '';
    j.async = true;
    j.src = `https://www.googletagmanager.com/gtm.js?id=${i}${dl}`;
    f.parentNode?.insertBefore(j, f);
  })(window, document, 'script', 'dataLayer', GTM_ID);

  gtmScriptInjected = true;
}
